import { useState } from 'react';
import { connect } from 'redux-bundler-react';
import { Alert, Link } from '@trussworks/react-uswds';

import { useFormContext } from 'react-hook-form';

import FileInput from '@components/file-input/FileInput';
import LinkButton from '@components/link/linkButton';
import ReadOnlyMap from '@components/map/readOnlyMap';

import { FileTypes, TemplateFiles } from '@src/utils/enums';
import { useGetGeometryDataMutation } from '@hooks';

const GdbTimeoutMsg =
  'We were unable to process the uploaded GDB file because it may be too complex. Please simplify the file and try again.';

const GdbGatewayMsg = 'We were unable to process the uploaded GDB file. Please try again.';
const GdbProcessingMsg = 'The GDB file could not be processed. Please try again.';

const ProjectLocationTabGDB = connect(
  'doDownloadFile',
  ({
    doDownloadFile,
    activeTab,
    deleteFileCallback = () => {},
    extent,
    geometry,
    locationType = 'Project',
    resetFieldsCallback = () => {},
  }) => {
    const { watch, clearErrors, setError, setValue } = useFormContext();
    const { mutateAsync: getGeometryDataAsync } = useGetGeometryDataMutation();

    const [selectedFile, setSelectedFile] = useState([]);
    const [inputKey, setInputKey] = useState(Date.now());

    const projectName = watch('projectName');

    const resetFileInput = () => {
      setSelectedFile([]);
      resetFieldsCallback();
      setInputKey(Date.now());

      setValue('projectLocationGDBFile', [], {
        shouldDirty: true,
        shouldValidate: false,
      });
    };

    const setProcessingError = (message) => {
      resetFileInput();

      setError('projectLocationGDBFile', {
        type: 'manual',
        message,
      });
    };

    const handleDelete = () => {
      deleteFileCallback();
      resetFileInput();
      clearErrors('projectLocationGDBFile');
    };

    const handleFileInput = async (event) => {
      const files = event?.target?.files;
      const filesArr = files ? Array.from(files) : [];

      if (filesArr.length === 0) {
        return;
      }

      setSelectedFile(filesArr);

      setValue('projectLocationGDBFile', filesArr, {
        shouldDirty: true,
        shouldValidate: false,
      });

      clearErrors('projectLocationGDBFile');

      try {
        const result = await getGeometryDataAsync({
          gdbFile: filesArr[0],
          firstPolygonOnly: true,
        });
        if (!result.success) {
          const fallbackMessage =
            result.reason === 'timeout'
              ? GdbTimeoutMsg
              : result.reason === 'gateway'
                ? GdbGatewayMsg
                : GdbProcessingMsg;
          setProcessingError(result.message ?? fallbackMessage);
        }
      } catch {
        setProcessingError(GdbProcessingMsg);
      }
    };

    return (
      <div className='grid-row grid-gap-2 margin-top-2'>
        <div className='desktop:grid-col-6 tablet:grid-col-12'>
          <h5 className='border-bottom'>{locationType} Map</h5>

          {activeTab === 0 && <ReadOnlyMap mapOnly extent={extent} geometry={geometry} geometryName={projectName} />}
        </div>

        <div className='desktop:grid-col-6 tablet:grid-col-12'>
          <h5 className='border-bottom'>Geodatabase File Upload</h5>

          <Alert className='margin-top-0' type='info' validation>
            <ul>
              <li>{locationType} Location geometries should be dissolved to a single row geometry</li>
              <li>Acceptable geometry types: Point, Line, Polygon, MultiPoint, MultiLine, MultiPolygon</li>
              <li>All geometries must use CRS(Coordinate Reference System) WGS84</li>
              <li>Geodatabases must be compressed into a single .zip file</li>
              <li>3D features are not supported</li>
              <li>
                If your GDB does not upload successfully, please take a screenshot of the error and send it, along with
                the GDB, to <Link href='mailto:rrs@usace.army.mil'>rrs@usace.army.mil</Link>
              </li>
              <li>
                Use the GDB upload template located here:{' '}
                <LinkButton
                  onClick={() => doDownloadFile(TemplateFiles.ProjectLocationGDBTemplate, FileTypes.Template)}
                  title='GDB Template [ZIP, 24 KB]'
                  content='GDB Template [ZIP, 24 KB]'
                />
              </li>
            </ul>
          </Alert>

          <div className='margin-top-3 margin-bottom-3'>
            <FileInput
              key={inputKey}
              accept='.zip'
              deleteFileCallback={handleDelete}
              id='projectLocationGDBFile'
              dragText='Drag .gdb (zipped) file here or '
              hint='Compressed GDB (.zip) files must be 5 MiB or smaller.'
              name='projectLocationGDBFile'
              onChangeCallback={handleFileInput}
              onDropCallback={handleFileInput}
              pickerCloseErrorMessage='Geodatabase File Upload: Please select a file to upload'
              required
              validateOnPickerClose
              value={selectedFile}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default ProjectLocationTabGDB;
